import * as React from 'react';
import { cn } from '@relay-towing/tokens';
import {
  dropzoneRecipe,
  type DropzoneRecipeProps,
} from '../recipes/media.recipe';

export interface DropzoneProps
  extends Omit<React.HTMLAttributes<HTMLDivElement>, 'onDrop'>,
    DropzoneRecipeProps {
  /**
   * Spread from the file-drop library's root props — `getRootProps()` in
   * react-dropzone. The library is not a dependency here: which one you use,
   * and what it accepts, is the product's decision. This owns the markup.
   */
  rootProps?: React.HTMLAttributes<HTMLDivElement>;
  /** The hidden input's props — `getInputProps()`. */
  inputProps?: React.InputHTMLAttributes<HTMLInputElement>;
  /** True while a file is over the target. Drives the highlight. */
  active?: boolean;
  children: React.ReactNode;
}

/**
 * A file drop target.
 *
 * The hidden `<input type="file">` lives here rather than at the call site.
 * It has to be a raw input: the drop library supplies its ref, its change
 * handler and `display: none`, and its props include the HTML `size`
 * attribute, which the library `Input` narrows to a control size. It is a
 * mechanism rather than a control — which is exactly the kind of thing that
 * belongs behind a component instead of being explained in five files.
 *
 *   const { getRootProps, getInputProps, isDragActive } = useDropzone({ onDrop });
 *
 *   <Dropzone
 *     rootProps={getRootProps()}
 *     inputProps={getInputProps()}
 *     active={isDragActive}
 *   >
 *     <ImagePlus />
 *     <Text variant="caption" tone="muted">Drop a logo</Text>
 *   </Dropzone>
 */
export const Dropzone = React.forwardRef<HTMLDivElement, DropzoneProps>(
  function Dropzone(
    { rootProps, inputProps, active, height, className, children, ...props },
    ref
  ) {
    return (
      <div
        ref={ref}
        {...rootProps}
        {...props}
        className={cn(dropzoneRecipe({ active, height }), className)}
      >
        {/* eslint-disable-next-line no-restricted-syntax -- see the note above:
            the drop library owns this input entirely. */}
        <input {...inputProps} />
        {children}
      </div>
    );
  }
);
